import React, { useContext, useEffect, useRef, useState } from 'react';
import { useForm, SubmitHandler, set } from "react-hook-form";
import { toast } from 'react-toastify';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useRotaBase from '../../utils/useRotaBase';

import { FaSave, FaSearch } from 'react-icons/fa';

import { Acoes, CampoFicha, Container, InputsContainer } from './styles';

import FormInput from '../FormInput';
import Checkbox from '../Checkbox';
import Select, { IOpcao } from '../Select';
import AutoCompleteInput from '../AutoCompleteInput';
import Button from '../Button';
import GridGenerico from '../GridGenerico';
import FotosProduto from '../FotosProduto';
import GradeProduto from '../GradeProduto';
import { IGradeSelecao } from '../../types/Grade';
import { valorInput } from '../Tabela/styles';

import { IMercador } from '../../types/Mercador';
import { ICliente } from '../../types/Cliente';
import api from '../../services/api';
import { buscarProximaReferencia } from '../../services/produto';

import Context, { IContext } from '../../context/Context';
import { IPedSai } from '../../types/PedSai';
import { getConfigRelatorio } from '../../constants/relatorios';
import { tratarPayload } from '../../utils/payload';
import { buscarCep, buscarCidadePorIbge } from '../../utils/cep';
import { mascaraPorTipo } from '../../utils/mascaras';

export interface Campo {
  label: string;
  campo: string;
  lim?: string;
  tipinp?: 'autocomplete' | 'button' | 'checkbox' | 'leitura' | 'date' | 'cep' | 'cgc' | 'ncm' | 'select'
  porfic?: string;
  obr?: string;
  /** laytel.pre: casas decimais dos números */
  pre?: string;
  /** tipinp 'select': opções de laytel.funval ('valor=rotulo;...'); a primeira é o padrão */
  opcoes?: IOpcao[];
}

interface InputPorTipoProps {
  tipo?: Campo['tipinp'];
  campo: Campo;
  index: number;
  dados: IMercador | ICliente | IPedSai | Record<string, string>;
  register: any;
  watch: any;
  setValue: any;
  // Filtros de relatório: campos com 'cod' no nome (codcat, codban...) são editáveis
  relatorio?: boolean;
  // tipinp 'cep': chamado no blur com o valor digitado
  onBlurCep?: (cep: string) => void;
  // Cidade achada pelo CEP, aplicada no autocomplete de Cidade
  cidadeCep?: Record<string, any>;
  // Entidade da ficha (o autocomplete de Fantasia busca cliente ou fornecedor)
  entidade?: string;
  // Campos de texto: chamado no blur com o nome do campo e o valor digitado
  onBlurCampo?: (nome: string, valor: string) => void;
}

export function InputPorTipo({ tipo, campo, dados, register, watch, setValue, relatorio, onBlurCep, cidadeCep, entidade, onBlurCampo }: InputPorTipoProps) {
  switch (tipo) {
    case 'autocomplete':
      return (
        <CampoFicha $porfic={campo.porfic}>
          <AutoCompleteInput
            label={campo.label}
            name={campo.campo}
            register={register}
            setFormValue={setValue}
            // @ts-ignore
            defaultValue={dados[campo?.campo]}
            selecionado={campo.label === 'Cidade' ? cidadeCep : undefined}
            entidade={entidade}
          />
        </CampoFicha>
      );
    // Máscara na digitação; o CEP também busca o endereço ao sair do campo
    case 'cep':
    case 'cgc':
    case 'ncm': {
      const registro = register(campo.campo);
      const mascara = mascaraPorTipo(tipo)!;
      return (
        <CampoFicha $porfic={campo.porfic}>
          <FormInput
            placeHolder={campo.label}
            id={campo.campo}
            // @ts-ignore
            defaultValue={mascara.aplicar(dados[campo?.campo] || '')}
            maxLength={mascara.tamanho}
            inputMode='numeric'
            required={campo.obr === '1'}
            {...registro}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              e.target.value = mascara.aplicar(e.target.value);
              registro.onChange(e);
            }}
            onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
              registro.onBlur(e);
              if (tipo === 'cep') onBlurCep?.(e.target.value);
            }}
          />
        </CampoFicha>
      );
    }
    case 'button':
      return (
        <></>
      );
    case 'select':
      return (
        <CampoFicha $porfic={campo.porfic}>
          <Select
            label={campo.label}
            id={campo.campo}
            opcoes={campo.opcoes ?? []}
            // @ts-ignore
            defaultValue={dados[campo?.campo] ?? campo.opcoes?.[0]?.valor}
            required={campo.obr === '1'}
            {...register(campo.campo)}
          />
        </CampoFicha>
      );
    case 'checkbox':
      return (
        <CampoFicha $porfic={campo.porfic}>
          <Checkbox
            label={campo.label}
            id={campo.campo}
            {...register(campo.campo)}
            checked={!!watch(campo.campo) && watch(campo.campo) !== '0'}
          />
        </CampoFicha>
      );

    default: {
      const registro = register(campo.campo);
      return (
        <CampoFicha $porfic={campo.porfic}>
          <FormInput
            placeHolder={campo.label}
            id={campo.campo}
            // @ts-ignore
            defaultValue={campo.tipinp === 'date' ? dados[campo?.campo] ? dados[campo?.campo].split('/').reverse().join('-') : '' : typeof dados[campo?.campo] === 'number' ? valorInput(dados[campo?.campo], campo.pre) : dados[campo?.campo] || ''}
            readOnly={(!relatorio && campo.campo.includes('cod') && campo.campo.toLowerCase() !== 'codbar') || campo?.tipinp === 'leitura'}
            maxLength={campo?.lim ? Number(campo.lim) : undefined}
            type={campo?.tipinp ? campo.tipinp : 'text'}
            // Sem step o navegador assume 1 e recusa decimais (ex.: 99,9 em valven/valcus)
            step={(campo?.tipinp as string) === 'number' ? 'any' : undefined}
            required={campo.obr === '1'}
            {...registro}
            onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
              registro.onBlur(e);
              onBlurCampo?.(campo.campo, e.target.value);
            }}
          />
        </CampoFicha>
      );
    }
  }
}

interface FichaCadastroGenericoProps {
  entidade: string;
  campos: Campo[];
  dados: IMercador | ICliente | IPedSai;
}

export default function FichaCadastroGenerico({ entidade, campos, dados }: FichaCadastroGenericoProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const rotaBase = useRotaBase();
  const { setIsLoadingOverlay, empresa }: IContext = useContext(Context);
  const configRelatorio = getConfigRelatorio(entidade);

  const [dadosGridGenerico, setDadosGridGenerico] = useState<Record<string, any>[]>([]);
  // Recarrega o grid depois de gravar: os itens novos ganham cod e a quantidade gravada vira a base da próxima diferença
  const [recarregarItens, setRecarregarItens] = useState(0);

  // Grade do produto (cor x tamanho): só vai no payload quando o usuário mudou alguma coisa
  const usaGrade = entidade.toLowerCase() === 'produto' && !!empresa?.grade;
  const [gradeAlterada, setGradeAlterada] = useState<IGradeSelecao>();
  const [recarregarGrade, setRecarregarGrade] = useState(0);

  const { register, handleSubmit, watch, setValue, getValues, formState: { errors } } = useForm<any>();

  const movimento = entidade === 'vendas' || entidade === 'compras';

  // Campo da ficha pelo nome, sem diferenciar maiúsculas
  const acharCampo = (nome: string) => campos.find(campo => campo.campo.toLowerCase() === nome);

  // Campo de data vazio (datcad, dat...) recebe a data de hoje, no formato do input
  const preencherDataHoje = (nome: string) => {
    const campoData = acharCampo(nome);
    if (!campoData || getValues(campoData.campo)) return;

    const hoje = new Date();
    const dia = String(hoje.getDate()).padStart(2, '0');
    const mes = String(hoje.getMonth() + 1).padStart(2, '0');
    const ano = hoje.getFullYear();

    setValue(campoData.campo, campoData.tipinp === 'date' ? `${ano}-${mes}-${dia}` : `${dia}/${mes}/${ano}`);
  };

  // Venda/compra nova já abre com a data de hoje
  useEffect(() => {
    // @ts-ignore
    if (movimento && !dados?.cod) preencherDataHoje('dat');
  }, [campos, entidade]);

  // Produto novo já abre com a referência sugerida (última + 1), se o usuário ainda não digitou nada
  useEffect(() => {
    // @ts-ignore
    if (entidade.toLowerCase() !== 'produto' || dados?.cod) return;
    const campoRef = acharCampo('codbar');
    if (!campoRef || getValues(campoRef.campo)) return;

    let ativo = true;
    buscarProximaReferencia().then(referencia => {
      if (ativo && referencia && !getValues(campoRef.campo)) setValue(campoRef.campo, referencia);
    });
    return () => { ativo = false; };
  }, [campos, entidade]);

  // Itens atuais, para o recálculo disparado pelos campos de desconto/juros/frete
  const itensRef = useRef(dadosGridGenerico);
  itensRef.current = dadosGridGenerico;

  const numero = (valor: any) => Number(String(valor ?? '').replace(',', '.')) || 0;

  // Mesma conta que a API faz ao gravar: valbru = soma(qua*val) + frete e valliq = valbru - valdes + valjur.
  // Só atualiza os campos que existem na ficha (compras não tem valliq/valjur)
  const recalcularTotais = (itens: Record<string, any>[]) => {
    const valorCampo = (nome: string) => {
      const campo = acharCampo(nome);
      return campo ? numero(getValues(campo.campo)) : 0;
    };
    const arredondar = (valor: number) => (Math.round(valor * 100) / 100).toFixed(2);

    const somaItens = itens
      .filter(item => item?.exc !== 'true')
      .reduce((soma, item) => soma + numero(item.qua) * numero(item.val), 0);
    const valbru = somaItens + valorCampo('valfre');

    const campoBru = acharCampo('valbru');
    if (campoBru) setValue(campoBru.campo, arredondar(valbru));

    const campoLiq = acharCampo('valliq');
    if (campoLiq) setValue(campoLiq.campo, arredondar(valbru - valorCampo('valdes') + valorCampo('valjur')));
  };

  // Digitar desconto, juros ou frete recalcula os totais
  useEffect(() => {
    if (!movimento) return;

    const assinatura = watch((_valores: any, { name, type }: { name?: string; type?: string }) => {
      if (type === 'change' && name && ['valdes', 'valjur', 'valfre'].includes(name.toLowerCase())) {
        recalcularTotais(itensRef.current);
      }
    });
    return () => assinatura.unsubscribe();
  }, [watch, campos, entidade]);

  // Último CEP buscado: sair do campo sem mudar o CEP não sobrescreve o endereço já digitado
  const ultimoCep = useRef(String((dados as any)?.cep || '').replace(/\D/g, ''));
  const [cidadeCep, setCidadeCep] = useState<Record<string, any>>();

  // tipinp 'cep': ViaCEP preenche logradouro e bairro; a cidade vem do cadastro pelo codibg
  const preencherPorCep = async (valor: string) => {
    const cep = valor.replace(/\D/g, '');
    if (cep.length !== 8 || cep === ultimoCep.current) return;
    ultimoCep.current = cep;

    try {
      setIsLoadingOverlay(true);

      const endereco = await buscarCep(cep);
      if (!endereco) {
        toast.warning('CEP não encontrado');
        return;
      }

      // CEP geral da cidade vem sem logradouro/bairro: mantém o que já estava
      const preencher = (nome: string, conteudo: string) => {
        const campo = campos.find(c => c.campo.toLowerCase() === nome);
        if (campo && conteudo) setValue(campo.campo, conteudo.toUpperCase());
      };
      preencher('log', endereco.logradouro);
      preencher('bai', endereco.bairro);

      const cidade = await buscarCidadePorIbge(endereco.localidade, endereco.ibge);
      if (cidade) setCidadeCep(cidade);
      else toast.warning(`Cidade ${endereco.localidade}/${endereco.uf} não encontrada no cadastro`);

    } catch (error) {
      console.error(error);
      // Permite tentar de novo saindo do campo
      ultimoCep.current = '';
      toast.error('Erro ao buscar o CEP');

    } finally {
      setTimeout(() => {
        setIsLoadingOverlay(false);
      }, 200);
    }
  };

  // Cliente/fornecedor: como no ERP desktop, sair da Razão com a Fantasia vazia copia a razão para a fantasia
  const preencherFantasia = (nome: string, valor: string) => {
    if (!['cliente', 'fornecedor'].includes(entidade.toLowerCase()) || nome.toLowerCase() !== 'raz' || !valor.trim()) return;

    const campoFan = acharCampo('fan');
    if (campoFan && !String(getValues(campoFan.campo) ?? '').trim()) setValue(campoFan.campo, valor);
  };

  const onSubmit: SubmitHandler<any> = async data => {
    try {
      setIsLoadingOverlay(true);

      let endpoint = entidade === 'produto' ? 'mercador' : entidade;

      switch (entidade.toLowerCase()) {
        case 'produto':
          endpoint = '/mercador';
          break;
        case 'cliente':
          endpoint = '/cliente';
          break;
        case 'fornecedor':
          endpoint = '/forneced';
          break;
        case 'vendas':
          endpoint = '/pedsai';
          break;
        case 'compras':
          endpoint = '/pedent';
          break;
        default:
          endpoint = configRelatorio ? '/relatorio' : 'naoencontrado';
          break;
      }

      if (endpoint === 'naoencontrado') {
        toast.warning('Não foi encontrado o endpoint da entidade');
        alert(JSON.stringify(data, null, 2));
        return;
      }

      // O cod nem sempre é campo do laytel (mercador): sem ele a edição viraria POST e duplicaria o registro
      if (!data?.cod && (dados as any)?.cod) data.cod = String((dados as any).cod);

      const payload = tratarPayload(data);

      // Depois do tratarPayload, que não deve mexer no objeto da grade
      if (usaGrade && gradeAlterada) payload.grade = gradeAlterada;

      if (dadosGridGenerico.length > 0) {
        // Na edição a API soma a qua recebida à gravada (o tifireapp manda só o que entrou na mesa):
        // vai só a diferença, e itens sem mudança ficam de fora (senão também passariam pela validação de estoque)
        payload.itens = dadosGridGenerico
          .map(item => ({
            item,
            qua: item?.cod && !item?.exc ? +item.qua - (+item._quaori || 0) : +item.qua
          }))
          .filter(({ item, qua }) => !item?.cod || item?.exc || qua !== 0 || +item.val !== +item._valori)
          .map(({ item, qua }) => (
            {
              codmer: item.codmer.toString(),
              qua: qua.toString(),
              val: item.val.toString(),
              ...(item?.cod && { cod: +item.cod }),
              ...(item?.exc && { exc: 'true' })
            }
          ));
      }

      console.log(payload);

      if (data?.cod) {
        const response = await api.put(endpoint, payload);

        if (response.status === 200) {
          toast.success('Atualizado com sucesso');
          setRecarregarItens(n => n + 1);
          setRecarregarGrade(n => n + 1);
          // Na grade, a combinação do produto principal pode ter saído: outra variação vira o principal
          const codNovo = response.data?.cod;
          if (codNovo && String(codNovo) !== String(data.cod)) {
            // @ts-ignore
            dados.cod = codNovo;
            setValue('cod', codNovo);
            navigate(location.pathname.replace(/\/[^/]+$/, `/${codNovo}`), { replace: true });
          }
          return;
        }
      }

      const response = await api.post(endpoint, payload, {
        params: {
          ...(endpoint === '/relatorio' && { rel: configRelatorio?.rel })
        }
      });

      if (endpoint === '/relatorio') {
        if (response.status === 200) {
          navigate(`${rotaBase}/relatorio/${entidade}`, { state: { data: response.data, filtros: payload, campos } });
        } else if (response.status === 204) {
          toast.info('Nenhum dado encontrado para os filtros informados');
        }
        return;
      }

      if (response.status === 201) {
        toast.success('Cadastrado com sucesso');
        setRecarregarItens(n => n + 1);
        setRecarregarGrade(n => n + 1);
        dados.cod = response.data.cod;
        // Mostra o código gerado na ficha; o próximo salvar já vira PUT
        setValue('cod', response.data.cod);
        preencherDataHoje('datcad');
        // .../novo → .../<cod> (cadastro/ ou comercial/), para recarregar a página abrir o registro gravado
        if (response.data.cod && /\/novo$/.test(location.pathname)) {
          navigate(location.pathname.replace(/\/novo$/, `/${response.data.cod}`), { replace: true });
        }
        return;
      }

    } catch (error: any) {
      console.error(error);
      // A API devolve 400 com {STATUS:'ERRO', MENSAGEM} nas validações (ex.: CPF/CNPJ repetido)
      toast.error(error.response?.data?.MENSAGEM || 'Erro ao cadastrar ou atualizar');

    } finally {
      setTimeout(() => {
        setIsLoadingOverlay(false);
      }, 500);
    }
  };

  const botoes = campos.filter(campo => campo.tipinp === 'button');

  return (
    <Container onSubmit={handleSubmit(onSubmit)}>
      <InputsContainer>
        {campos.map((campo, index) => (
          <InputPorTipo key={`${campo.campo}-${index}`} tipo={campo?.tipinp} campo={campo} index={index} dados={dados} register={register} watch={watch} setValue={setValue} relatorio={!!configRelatorio} onBlurCep={preencherPorCep} cidadeCep={cidadeCep} entidade={entidade} onBlurCampo={preencherFantasia} />
        ))}
      </InputsContainer>
      {movimento &&
        <GridGenerico
          entidade={entidade === 'vendas' ? 'itepedsa' : 'itepeden'}
          //@ts-ignore
          codigo={dados.cod}
          dadosGridGenerico={dadosGridGenerico}
          setDadosGridGenerico={setDadosGridGenerico}
          recarregar={recarregarItens}
          onItensAlterados={recalcularTotais}
          botoesVisiveis
        />}

      {usaGrade &&
        <GradeProduto
          // @ts-ignore
          codmer={dados?.cod}
          recarregar={recarregarGrade}
          onChange={setGradeAlterada}
        />}

      {entidade.toLowerCase() === 'produto' &&
        <FotosProduto
          // @ts-ignore
          codmer={dados?.cod}
          // @ts-ignore
          codbar={dados?.codbar}
          // @ts-ignore
          codpad={dados?.codpad}
        />}

      <Acoes>
        {botoes.length > 0 ?
          botoes.map(campo =>
            <Button
              key={campo.campo}
              texto={campo.label}
              type='submit'
            />)
          :
          <Button
            texto={configRelatorio ? 'Consultar' : 'Salvar'}
            icone={configRelatorio ? <FaSearch /> : <FaSave />}
            type='submit'
          />
        }
      </Acoes>
    </Container>
  );
}
